import {
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
    type SubmitEvent,
    type FormHTMLAttributes,
    type RefObject,
} from 'react'
import { ResponseJsonKeyEnum } from '@/Shared/AutoGenerated/Main'
import ApiFormResponseType from '@/Shared/Component/Forms/ApiFormResponseType'

type ApiFormMethod = 'GET' | 'POST'

interface ApiFormHandle {
    submit(): Promise<ApiFormResponseType> | null
    isInvalid(): boolean
    reportInvalid(): boolean
}

export interface ApiFormState {
    readonly formRef: RefObject<ApiFormHandle | null>
    readonly loading: boolean
    readonly response: ApiFormResponseType | null
    submit(): Promise<ApiFormResponseType> | null
    isInvalid(): boolean
    reportInvalid(): boolean
    setLoading(loading: boolean): void
    setResponse(response: ApiFormResponseType): void
}

export interface ApiFormProps extends Omit<
    FormHTMLAttributes<HTMLFormElement>,
    'action' | 'method' | 'onSubmit'
> {
    apiForm: ApiFormState
    method?: ApiFormMethod
    url: string
    disableCatch?: boolean
    onResponse?: (response: ApiFormResponseType, httpStatusCode: number) => void
    onSubmit?: (event: SubmitEvent<HTMLFormElement>) => void
}

export function useApiForm(): ApiFormState {
    const formRef = useRef<ApiFormHandle | null>(null)
    const [loading, setLoading] = useState(false)
    const [response, setResponse] = useState<ApiFormResponseType | null>(null)

    const submit = useCallback((): Promise<ApiFormResponseType> | null => {
        return formRef.current?.submit() ?? null
    }, [])

    const isInvalid = useCallback((): boolean => {
        if (!formRef.current) {
            throw new Error('Api form handle is missing.')
        }

        return formRef.current.isInvalid()
    }, [])

    const reportInvalid = useCallback((): boolean => {
        if (!formRef.current) {
            throw new Error('Api form handle is missing.')
        }

        return formRef.current.reportInvalid()
    }, [])

    return {
        formRef,
        loading,
        response,
        submit,
        isInvalid,
        reportInvalid,
        setLoading,
        setResponse,
    }
}

export function ApiForm({
    method = 'POST',
    url,
    disableCatch = true,
    apiForm,
    onResponse,
    children,
    onSubmit,
    ...formAttributes
}: ApiFormProps) {
    const formRef = useRef<HTMLFormElement | null>(null)

    function setLoadingState(loading: boolean): void {
        apiForm.setLoading(loading)
    }

    function isInvalid(): boolean {
        if (!formRef.current) {
            throw new Error('Api form element is missing.')
        }

        return !formRef.current.checkValidity()
    }

    function reportInvalid(): boolean {
        if (!formRef.current) {
            throw new Error('Api form element is missing.')
        }

        return formRef.current.reportValidity()
    }

    function submit(): Promise<ApiFormResponseType> | null {
        const form = formRef.current

        if (!form) {
            throw new Error('Api form element is missing.')
        }

        if (isInvalid()) {
            reportInvalid()

            return null
        }

        setLoadingState(true)

        return new Promise<ApiFormResponseType>((resolve, reject) => {
            const request = new XMLHttpRequest()

            request.onreadystatechange = function (): void {
                if (request.readyState !== XMLHttpRequest.DONE) {
                    return
                }

                const responseData = JSON.parse(
                    request.responseText || '{}',
                ) as unknown
                const apiFormResponse = new ApiFormResponseType(responseData)

                apiForm.setResponse(apiFormResponse)
                setLoadingState(false)
                onResponse?.(apiFormResponse, request.status)

                if (request.status === 302) {
                    const redirectUrl = apiFormResponse.getStringFromPayload(
                        ResponseJsonKeyEnum.RedirectUrl,
                    )

                    window.location.assign(redirectUrl)
                    resolve(apiFormResponse)

                    return
                }

                if (request.status === 422 && disableCatch) {
                    resolve(apiFormResponse)

                    return
                }

                if (request.status >= 200 && request.status < 300) {
                    resolve(apiFormResponse)

                    return
                }

                if (disableCatch) {
                    resolve(apiFormResponse)

                    return
                }

                reject(apiFormResponse)
            }

            request.open(method, url, true)
            request.setRequestHeader('Accept', 'application/json')
            request.send(new FormData(form))
        })
    }

    function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
        event.preventDefault()
        onSubmit?.(event)

        void submit()
    }

    useImperativeHandle(
        apiForm.formRef,
        () => ({
            submit,
            isInvalid,
            reportInvalid,
        }),
        [apiForm],
    )

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            {...formAttributes}
        >
            {children}
        </form>
    )
}
